import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {OmniContext} from "@/OmniContext.ts";
import {useDisclosure, useMediaQuery, useViewportSize} from "@mantine/hooks";
import classes from "./SidebarLinks.module.css";
import {AppShell, Center, Group, Loader, ScrollArea} from "@mantine/core";
import {LinksGroup} from "./LinksGroup/LinksGroup.tsx";
import {IconPencil, IconPencilOff, IconPlus, IconStar} from "@tabler/icons-react";
import TooltipActionIcon from "@/components/TooltipActionIcon.tsx";
import {openLinkModal} from "@/components/SidebarLinks/LinkModal/modal_utils.tsx";
import getInternalLinks from "@/util/InternalLinks.js";
import OmniFetchTS from "@/util/OmniFetchTS.tsx";
import { IGetLinks } from "@/types/api/Link.ts";
import {ILink} from "@/types/models/ILink.ts";
import {ILinkCategoryWithLinks} from "@/types/models/ILinkCategory.ts";
import {SysPerm} from "@/util/SysPerm.ts";
import openManageFavoriteLinksModal from "@/components/SidebarLinks/ManageFavoriteLinksModal/modal_utils.tsx";

export default function SidebarLinks({toggleSidebar}: {toggleSidebar: () => void}) {
    const [linkData, setLinkData] = useState<ILinkCategoryWithLinks[] | null>(null)
    const [editMode, {toggle: toggleEditMode}] = useDisclosure(false)
    const context = useContext(OmniContext)
    const { height } = useViewportSize()
    const isMobile = useMediaQuery(`(max-width: 48em)`);

    const handleInternalNavigation = useCallback(() => {
        if (isMobile) {
            toggleSidebar()
        }
    }, [isMobile, toggleSidebar])

    const reloadLinks = useCallback(() => {
        const internalLinks = context.user ? getInternalLinks(context.user) : []
        OmniFetchTS<IGetLinks>("/api/v1/links").then((json) => {
            if (json.success && json.data) {
                let found_internal_links_category = false
                let internal_links_category_name = "Services on " + context.environment!.siteName
                let link_data = json.data.categories
                link_data.sort((a, b) => {
                    if (a.order < b.order) {
                        return 1
                    } else if (a.order === b.order) {
                        return 0
                    } else {
                        return -1
                    }
                })
                link_data.forEach((category) => {
                    if (category["name"] === internal_links_category_name) {
                        found_internal_links_category = true
                        internalLinks.forEach((link: ILink) => {
                            link.category_id = "internal"
                            category.links.push(link)
                        })
                    }
                    category.links.forEach((link) => {
                        link["category"] = category["name"]
                    })
                    category.links.sort((a, b) => {
                        if (a.category_id == "internal") {
                            return 0
                        } else if (a.order < b.order) {
                            return 1
                        } else if (a.order === b.order) {
                            return 0
                        } else {
                            return -1
                        }
                    })
                })
                if (!found_internal_links_category) {
                    link_data.push({
                        "id": -1,
                        "name": internal_links_category_name,
                        "links": internalLinks,
                        "order": -1,
                        "icon": "sparkles"
                    })
                }
                if (json.data.favorites.length) {
                    link_data.splice(0, 0, {
                        "id": -2,
                        "name": "Favorites",
                        "links": json.data.favorites,
                        "order": 100,
                        "icon": "star"
                    })
                }
                setLinkData(link_data)
            }
        })
    }, [context.user, context.environment])

    useEffect(() => {
        reloadLinks()
    }, [context.user, reloadLinks]);

    const editable = useMemo(() => context.user?.hasSystemPermission(SysPerm.LINK_ADMIN), [context.user])

    useEffect(() => {
        if (linkData == null) {
            reloadLinks()
        }
    }, [editable, context.user, reloadLinks, linkData]);

    return <OmniContext.Consumer>
        {({user}) => {
            if (linkData == null || user == null) {
                return <Center><Loader/></Center>
            } else {
                let highestCategoryId = 0
                let highestCategoryOrder = 0
                linkData.forEach((category) => {
                    if (category.order > highestCategoryOrder) {
                        highestCategoryOrder = category.order
                        highestCategoryId = category.id
                    }
                })
                return <nav className={classes.navbar}>
                    <AppShell.Section component={ScrollArea} className={classes.links} h={height} mb={"sm"}>
                        <div className={classes.linksInner} style={{width: isMobile ? "inherit" : "297px"}}>
                            {linkData.map((category) => {
                                return <LinksGroup key={`category${category.id}`} initiallyOpened={highestCategoryId === category.id} links={category.links} editMode={editMode} reloadLinks={reloadLinks} category={category} id={category.id} onInternalNavigation={handleInternalNavigation}></LinksGroup>
                            })}
                        </div>
                    </AppShell.Section>
                    <AppShell.Section><Group>
                        {editable && <>
                            <TooltipActionIcon onClick={() => {toggleEditMode()}} label={ editMode ? "Stop Editing" : "Edit Links"}>{editMode ? <IconPencilOff/> : <IconPencil/>}</TooltipActionIcon>
                            <TooltipActionIcon c={"green"} onClick={() => {openLinkModal(null, false, reloadLinks)}} label={"Add Link"}><IconPlus/></TooltipActionIcon>
                        </>}
                        <TooltipActionIcon label={"Manage Favorites"} onClick={() => openManageFavoriteLinksModal(linkData, reloadLinks)}><IconStar/></TooltipActionIcon>
                    </Group></AppShell.Section>
                </nav>
            }
        }}
    </OmniContext.Consumer>

}